/**
 * Register the service worker and notice when a new version is waiting.
 *
 * The registration itself is three lines; the reason this file exists is the
 * update path. A service worker that installs a new version and then waits for
 * every tab to close is the standard behaviour and the wrong one for an app
 * people leave open for days: they would run last month's build indefinitely
 * and nothing would tell them.
 *
 * So: when a new worker reaches "installed" while one is already controlling
 * the page, call `onUpdateReady` with a function that activates it and
 * reloads. What that looks like is the app's business, not this file's.
 */

/** Only where a service worker can exist at all: https, or localhost. */
export function isSupported(nav = navigator, loc = window.location, env = process.env.NODE_ENV) {
  if (!nav || !("serviceWorker" in nav)) return false;
  // Never on the dev server. localhost is a secure context, so this would
  // happily register there and then cache the unhashed dev bundles, which
  // breaks hot reload and leaves a worker running long after the dev server
  // is gone, serving a page whose API calls all fail.
  if (env === "development") return false;
  // The desktop app serves itself from 127.0.0.1, which is a secure context,
  // so this would happily register there. It must not: the app already ships
  // its own assets, and a cache in front of them would keep serving the old
  // build after an auto-update, which is the one thing auto-update exists to
  // prevent.
  if ((nav.userAgent || "").includes("Electron")) return false;
  return (
    loc.protocol === "https:" ||
    loc.hostname === "localhost" ||
    loc.hostname === "127.0.0.1" ||
    loc.hostname === "[::1]"
  );
}

/**
 * @param {(applyUpdate: () => void) => void} onUpdateReady
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker(onUpdateReady, options = {}) {
  const {
    nav = typeof navigator !== "undefined" ? navigator : null,
    loc = typeof window !== "undefined" ? window.location : null,
    reload = () => window.location.reload(),
    url = "/service-worker.js",
  } = options;

  if (!isSupported(nav, loc)) return null;

  let registration;
  try {
    registration = await nav.serviceWorker.register(url);
  } catch (err) {
    // Never fatal. A page that will not load because its cache layer failed
    // to register is strictly worse than a page with no cache layer.
    return null;
  }

  const apply = (worker) => () => {
    // Wait for the handover before reloading, not just for the message to be
    // sent. Posting and reloading in the same breath looks right and is not:
    // measured in Chrome, the page reloads before the worker has processed
    // SKIP_WAITING, so the new worker stays in "waiting", the old one keeps
    // control, and the user has clicked Reload for nothing.
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      reload();
    };

    nav.serviceWorker.addEventListener?.("controllerchange", go, { once: true });
    // If the handover never happens, reload anyway rather than leave a button
    // that did nothing.
    setTimeout(go, 3000);

    worker.postMessage({ type: "SKIP_WAITING" });
  };

  // Already waiting when the page loaded: the user updated in another tab, or
  // closed the app before accepting last time.
  if (registration.waiting && nav.serviceWorker.controller) {
    onUpdateReady(apply(registration.waiting));
  }

  registration.addEventListener?.("updatefound", () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      // "installed" with a controller already present means this is an
      // update. Without a controller it is the very first install, and
      // telling someone their brand new app has an update is nonsense.
      if (installing.state === "installed" && nav.serviceWorker.controller) {
        onUpdateReady(apply(installing));
      }
    });
  });

  return registration;
}
